import { useCallback } from "react";
import MAFSimulation from "./MAFSimulation";
import KintoSimulation from "./KintoSimulation";
import { KintoLogo } from "../Icons/KintoLogo";
import { ChevronRightIcon } from "../Icons/ChevronRightIcon";
import { ChevronDownIcon } from "../Icons/ChevronDownIcon";
import usePaymentOptions from "../../hooks/usePaymentOptions";
import useConfiguratorData from "../../hooks/useConfiguratorData";

/**
 * Selector de opciones de pago para v2
 * Maneja contado, financiamiento MAF y Kinto con sus respectivos simuladores
 */
const PaymentOptions = ({ hideSimulations = false, personalDataCompleted = false }) => {
    const {
        selectedPaymentOption,
        simulationResults,
        simulationKeys,
        paymentOptions,
        handleOptionChange,
        handleResultChange,
        handleEditSimulation,
    } = usePaymentOptions();

    const { getSelectedVersion } = useConfiguratorData();

    // Maneja la edición de simulación con prevención de propagación de eventos
    const handleEditSimulationWithEvent = useCallback(
        (optionId, event) => {
            event.stopPropagation();
            handleEditSimulation(optionId);
        },
        [handleEditSimulation]
    );

    const handleMAFResultChange = useCallback(
        (showingResult) => {
            handleResultChange(2, showingResult);
        },
        [handleResultChange]
    );

    const handleKintoResultChange = useCallback(
        (showingResult) => {
            handleResultChange(3, showingResult);
        },
        [handleResultChange]
    );

    const renderSimulationComponent = useCallback(
        (optionId, isHidden = false) => {
            switch (optionId) {
                case 2:
                    return (
                        <MAFSimulation
                            key={`maf-${simulationKeys[2]}`}
                            onResultChange={handleMAFResultChange}
                            isHidden={isHidden}
                            autoSimulateOnMount={personalDataCompleted}
                        />
                    );
                case 3:
                    return (
                        <KintoSimulation
                            key={`kinto-${simulationKeys[3]}`}
                            onResultChange={handleKintoResultChange}
                            isHidden={isHidden}
                        />
                    );
                default:
                    return null;
            }
        },
        [handleMAFResultChange, handleKintoResultChange, simulationKeys, personalDataCompleted]
    );

    const renderOptionContent = useCallback(
        (option) => {
            const isShowingResult = simulationResults[option.id];
            const displayName =
                isShowingResult && option.nameWithResult ? option.nameWithResult : option.name;

            return (
                <div className="visible-part">
                    <span className="option-name">
                        {displayName}
                        {option.hasLogo && (
                            <KintoLogo />
                        )}
                    </span>
                    {option.hasSimulation && !isShowingResult && <ChevronDownIcon />}
                    {isShowingResult && (
                        <a onClick={(e) => handleEditSimulationWithEvent(option.id, e)}>
                            Editar <ChevronRightIcon />
                        </a>
                    )}
                </div>
            );
        },
        [simulationResults, handleEditSimulationWithEvent]
    );
    return (
        <div className="payment-options">
            <div className="component-header">
                <h2>Opciones de Pago</h2>
                <p>Selecciona la opción de pago que mejor se adapte a tus necesidades.</p>
            </div>
            <div className="component-body">
                {paymentOptions.map((option) => (
                    <div
                        className={`item-box ${
                            selectedPaymentOption === option.id ? "active" : ""
                        }`}
                        key={`payment-option-${option.id}`}
                        onClick={() => handleOptionChange(option.id, option.name)}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedPaymentOption === option.id}
                        aria-label={`Seleccionar ${option.name}: ${option.description}`}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleOptionChange(option.id);
                            }
                        }}
                    >
                        {renderOptionContent(option)}
                        {option.hasSimulation && (
                            <div
                                className={`hidden-part ${
                                    selectedPaymentOption === option.id ? "active" : ""
                                }`}
                                role="region"
                                aria-label={`Simulador de ${option.name}`}
                            >
                                {renderSimulationComponent(option.id, hideSimulations)}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PaymentOptions;
